import { useState, useMemo } from "react";
import { cn } from "@/lib/utils";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Plus,
  Pencil,
  Trash2,
  RotateCw,
  BookOpen,
  IndianRupee,
  ShieldCheck,
  CheckCircle2,
  XCircle,
  Clock,
  FileText,
  Video as VideoIcon,
  HelpCircle,
  ShoppingBag,
  ExternalLink,
  Layers,
  Sparkles
} from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { PageHeader } from "@/components/common/PageHeader";
import { SearchBar } from "@/components/common/SearchBar";
import { DataTable, Column } from "@/components/tables/DataTable";
import { ConfirmDeleteDialog } from "@/components/common/ConfirmDeleteDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  useGetChaptersQuery,
  useCreateChapterMutation,
  useUpdateChapterMutation,
  useDeleteChapterMutation,
  Chapter
} from "@/redux/slices/chapterApi";
import { useGetAcademicYearsQuery } from "@/redux/slices/academicYearApi";
import { useGetSubjectsQuery } from "@/redux/slices/subjectApi";
import { useGetOrdersQuery, Order } from "@/redux/slices/orderApi";
import { format } from "date-fns";

const chapterSchema = z.object({
  name: z.string().trim().min(2, "Chapter name required").max(150),
  number: z.coerce.number().int().min(1, "Chapter number must be >= 1"),
  academicYearId: z.string().min(1, "Academic Year is required"),
  subjectId: z.string().min(1, "Subject is required"),
  price: z.coerce.number().min(0, "Price must be >= 0"),
  description: z.string().max(500).optional().default(""),
  isActive: z.boolean().default(true),
});
type ChapterFormValues = z.infer<typeof chapterSchema>;

const quickPriceSchema = z.object({
  price: z.coerce.number().min(0, "Price must be >= 0"),
});
type QuickPriceFormValues = z.infer<typeof quickPriceSchema>;

export default function PlanManagementPage() {
  const [activeTab, setActiveTab] = useState<"chapters" | "orders">("chapters");
  const [selectedYearId, setSelectedYearId] = useState<string>("all");
  const [selectedSubjectId, setSelectedSubjectId] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState("");

  const [chapterPage, setChapterPage] = useState(1);
  const [chapterLimit, setChapterLimit] = useState(10);
  const [orderPage, setOrderPage] = useState(1);
  const [orderLimit, setOrderLimit] = useState(10);

  // Data queries
  const { data: yearsData } = useGetAcademicYearsQuery({ limit: 100 });
  const { data: subjectsData } = useGetSubjectsQuery({ limit: 500 });
  const {
    data: chaptersData,
    isFetching: isFetchingChapters,
    refetch: refetchChapters
  } = useGetChaptersQuery({
    limit: 500,
    academicYearId: selectedYearId !== "all" ? selectedYearId : undefined,
    subjectId: selectedSubjectId !== "all" ? selectedSubjectId : undefined,
    search: searchQuery || undefined
  });

  const {
    data: ordersData,
    isFetching: isFetchingOrders,
    refetch: refetchOrders
  } = useGetOrdersQuery({
    limit: 100,
    search: searchQuery || undefined
  });

  // Mutations
  const [createChapter, createStatus] = useCreateChapterMutation();
  const [updateChapter, updateStatus] = useUpdateChapterMutation();
  const [deleteChapter, deleteStatus] = useDeleteChapterMutation();

  // Modals state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingChapter, setEditingChapter] = useState<Chapter | null>(null);
  const [toDeleteChapter, setToDeleteChapter] = useState<Chapter | null>(null);

  const [priceModalChapter, setPriceModalChapter] = useState<Chapter | null>(null);

  // Forms
  const form = useForm<ChapterFormValues>({
    resolver: zodResolver(chapterSchema),
    defaultValues: {
      name: "",
      number: 1,
      academicYearId: "",
      subjectId: "",
      price: 199,
      description: "",
      isActive: true,
    },
  });

  const priceForm = useForm<QuickPriceFormValues>({
    resolver: zodResolver(quickPriceSchema),
    defaultValues: { price: 0 },
  });

  const formYearId = form.watch("academicYearId");
  const filteredSubjectsForModal = useMemo(() => {
    if (!subjectsData?.data) return [];
    if (!formYearId) return subjectsData.data;
    return subjectsData.data.filter((s: any) => s.academicYearId === formYearId);
  }, [subjectsData, formYearId]);

  const filteredSubjectsForFilter = useMemo(() => {
    if (!subjectsData?.data) return [];
    if (selectedYearId === "all") return subjectsData.data;
    return subjectsData.data.filter((s: any) => s.academicYearId === selectedYearId);
  }, [subjectsData, selectedYearId]);

  // Open Create Modal
  const openCreateModal = () => {
    setEditingChapter(null);
    const defaultYear = yearsData?.data?.[0]?.id || "";
    form.reset({
      name: "",
      number: (chaptersData?.data?.length || 0) + 1,
      academicYearId: defaultYear,
      subjectId: subjectsData?.data?.find((s: any) => s.academicYearId === defaultYear)?.id || "",
      price: 199,
      description: "",
      isActive: true,
    });
    setDialogOpen(true);
  };

  // Open Edit Modal
  const openEditModal = (ch: Chapter) => {
    setEditingChapter(ch);
    form.reset({
      name: ch.name,
      number: ch.number,
      academicYearId: ch.academicYearId,
      subjectId: ch.subjectId,
      price: ch.price !== undefined ? Number(ch.price) : 0,
      description: ch.description || "",
      isActive: ch.isActive,
    });
    setDialogOpen(true);
  };

  // Open Quick Price Modal
  const openQuickPriceModal = (ch: Chapter) => {
    setPriceModalChapter(ch);
    priceForm.reset({
      price: ch.price !== undefined ? Number(ch.price) : 0,
    });
  };

  // Submit Chapter Create/Update
  const onChapterSubmit = async (values: ChapterFormValues) => {
    try {
      if (editingChapter) {
        await updateChapter({ id: editingChapter.id, body: values }).unwrap();
        toast.success(`Chapter "${values.name}" updated successfully.`);
      } else {
        await createChapter(values).unwrap();
        toast.success(`Chapter plan "${values.name}" created with ₹${values.price} lifetime price.`);
      }
      setDialogOpen(false);
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to save chapter plan.");
    }
  };

  // Submit Quick Price
  const onPriceSubmit = async (values: QuickPriceFormValues) => {
    if (!priceModalChapter) return;
    try {
      await updateChapter({
        id: priceModalChapter.id,
        body: { price: values.price },
      }).unwrap();
      toast.success(`Updated price for "${priceModalChapter.name}" to ₹${values.price}`);
      setPriceModalChapter(null);
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to update price.");
    }
  };

  // Delete Chapter
  const handleDeleteChapter = async () => {
    if (!toDeleteChapter) return;
    try {
      await deleteChapter(toDeleteChapter.id).unwrap();
      toast.success(`Chapter "${toDeleteChapter.name}" deleted successfully.`);
      setToDeleteChapter(null);
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to delete chapter.");
    }
  };

  // Toggle Chapter Active State
  const toggleChapterStatus = async (ch: Chapter) => {
    try {
      await updateChapter({
        id: ch.id,
        body: { isActive: !ch.isActive },
      }).unwrap();
      toast.success(`Chapter ${!ch.isActive ? "activated" : "deactivated"} successfully.`);
    } catch {
      toast.error("Failed to update status.");
    }
  };

  // Columns for Chapters Table
  const chapterColumns: Column<Chapter>[] = [
    {
      key: "name",
      header: "Chapter Details",
      render: (ch) => (
        <div className="flex items-start gap-3 py-1">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border bg-primary/10 text-primary font-bold text-sm">
            {ch.number}
          </div>
          <div>
            <span className="block font-semibold text-foreground text-sm">{ch.name}</span>
            <span className="text-xs text-muted-foreground line-clamp-1">{ch.description || "No description"}</span>
          </div>
        </div>
      ),
    },
    {
      key: "academicYear",
      header: "Academic Year & Subject",
      render: (ch) => (
        <div className="flex flex-col gap-0.5 text-xs">
          <span className="font-medium text-foreground">{ch.subject?.name || "—"}</span>
          <span className="text-muted-foreground">{ch.academicYear?.name || "—"}</span>
        </div>
      ),
    },
    {
      key: "price",
      header: "Lifetime Price",
      render: (ch) => (
        <div className="flex items-center gap-2">
          <Badge
            variant="outline"
            className="font-bold text-sm bg-emerald-500/10 text-emerald-600 border-emerald-500/20 px-2.5 py-1 flex items-center gap-1"
          >
            <IndianRupee className="h-3.5 w-3.5" />
            {ch.price !== undefined ? ch.price : 0}
          </Badge>
          <Button
            size="sm"
            variant="ghost"
            className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
            onClick={() => openQuickPriceModal(ch)}
            title="Quick edit price"
          >
            Edit
          </Button>
        </div>
      ),
    },
    {
      key: "entitlement",
      header: "Access Model",
      render: () => (
        <div className="flex items-center gap-1.5 text-xs font-semibold text-primary">
          <ShieldCheck className="h-4 w-4 text-emerald-500" />
          <span>Individual Lifetime</span>
        </div>
      ),
    },
    {
      key: "contentLinks",
      header: "Chapter Content",
      render: (ch) => (
        <div className="flex items-center gap-2">
          <Link
            to={`/notes?chapterId=${ch.id}`}
            className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-muted hover:bg-muted/80 text-foreground transition-colors"
            title="Manage Notes & Demo PDF"
          >
            <FileText className="h-3 w-3 text-blue-500" />
            Notes
          </Link>
          <Link
            to={`/videos?chapterId=${ch.id}`}
            className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-muted hover:bg-muted/80 text-foreground transition-colors"
            title="Manage Video Lectures"
          >
            <VideoIcon className="h-3 w-3 text-red-500" />
            Videos
          </Link>
          <Link
            to={`/tests?chapterId=${ch.id}`}
            className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-muted hover:bg-muted/80 text-foreground transition-colors"
            title="Manage Chapter Tests"
          >
            <HelpCircle className="h-3 w-3 text-purple-500" />
            Tests
          </Link>
        </div>
      ),
    },
    {
      key: "isActive",
      header: "Status",
      render: (ch) => (
        <div className="flex items-center gap-2">
          <Switch
            checked={ch.isActive}
            onCheckedChange={() => toggleChapterStatus(ch)}
            className="data-[state=checked]:bg-emerald-600"
          />
          <span className="text-xs text-muted-foreground">
            {ch.isActive ? "Available" : "Disabled"}
          </span>
        </div>
      ),
    },
    {
      key: "actions",
      header: "Actions",
      className: "text-right",
      render: (ch) => (
        <div className="flex items-center justify-end gap-1">
          <Button
            size="sm"
            variant="ghost"
            className="h-8 w-8 p-0"
            onClick={() => openEditModal(ch)}
            title="Edit Chapter Details"
          >
            <Pencil className="h-4 w-4 text-muted-foreground hover:text-foreground" />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="h-8 w-8 p-0 text-destructive hover:bg-destructive/10"
            onClick={() => setToDeleteChapter(ch)}
            title="Delete Chapter"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ];

  // Columns for Purchases / Orders Table
  const orderColumns: Column<Order>[] = [
    {
      key: "student",
      header: "Student",
      render: (o) => (
        <div className="flex flex-col text-xs">
          <span className="font-semibold text-foreground text-sm">{o.user?.name || "Student"}</span>
          <span className="text-muted-foreground">{o.user?.mobile || o.user?.email || "—"}</span>
        </div>
      ),
    },
    {
      key: "chapter",
      header: "Purchased Chapter",
      render: (o: any) => {
        const ch = o.chapter;
        if (ch) {
          return (
            <div className="flex flex-col text-xs">
              <span className="font-semibold text-foreground">
                Ch. {ch.number}: {ch.name}
              </span>
              <span className="text-muted-foreground">{ch.subject?.name || "Subject"}</span>
            </div>
          );
        }
        return (
          <span className="text-xs text-muted-foreground">
            {o.plan?.title || "Legacy Plan"}
          </span>
        );
      },
    },
    {
      key: "amount",
      header: "Amount Paid",
      render: (o) => (
        <span className="font-bold text-sm text-foreground">
          ₹{o.amount}
        </span>
      ),
    },
    {
      key: "accessType",
      header: "Access Type",
      render: (o: any) => (
        <Badge variant="outline" className="text-xs bg-emerald-500/10 text-emerald-600 border-emerald-500/20">
          Lifetime Access
        </Badge>
      ),
    },
    {
      key: "paymentStatus",
      header: "Payment Status",
      render: (o) => {
        if (o.paymentStatus === "Success") {
          return (
            <Badge className="bg-emerald-600 text-white flex items-center gap-1 w-fit">
              <CheckCircle2 className="h-3 w-3" /> Success
            </Badge>
          );
        }
        if (o.paymentStatus === "Pending") {
          return (
            <Badge variant="secondary" className="flex items-center gap-1 w-fit">
              <Clock className="h-3 w-3" /> Pending
            </Badge>
          );
        }
        return (
          <Badge variant="destructive" className="flex items-center gap-1 w-fit">
            <XCircle className="h-3 w-3" /> Failed
          </Badge>
        );
      },
    },
    {
      key: "createdAt",
      header: "Purchased At",
      render: (o) => (
        <div className="flex flex-col text-xs text-muted-foreground">
          <span>{o.createdAt ? format(new Date(o.createdAt), "dd MMM yyyy") : "—"}</span>
          <span className="text-[10px] opacity-75">{o.transactionId || "—"}</span>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Chapter Purchase Plans"
        description="Configure chapter-wise lifetime purchase prices, unlock permissions, and monitor student purchases."
      >
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              refetchChapters();
              refetchOrders();
            }}
            className="gap-2"
          >
            <RotateCw className="h-4 w-4" />
            Refresh
          </Button>
          <Button size="sm" onClick={openCreateModal} className="gap-2">
            <Plus className="h-4 w-4" />
            Add Chapter Plan
          </Button>
        </div>
      </PageHeader>

      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as any)} className="w-full">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="chapters" className="flex items-center gap-2">
            <Layers className="h-4 w-4" />
            Chapter Pricing & Plans
          </TabsTrigger>
          <TabsTrigger value="orders" className="flex items-center gap-2">
            <ShoppingBag className="h-4 w-4" />
            Purchase Records
          </TabsTrigger>
        </TabsList>

        {/* TAB 1: CHAPTERS & PRICING */}
        <TabsContent value="chapters" className="space-y-4 pt-4">
          <div className="flex flex-col sm:flex-row gap-3 items-center justify-between">
            <div className="flex flex-1 flex-wrap items-center gap-3 w-full">
              {/* Year Filter */}
              <div className="w-full sm:w-48">
                <Select
                  value={selectedYearId}
                  onValueChange={(val) => {
                    setSelectedYearId(val);
                    setSelectedSubjectId("all");
                  }}
                >
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="All Academic Years" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Academic Years</SelectItem>
                    {yearsData?.data?.map((y: any) => (
                      <SelectItem key={y.id} value={y.id}>
                        {y.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Subject Filter */}
              <div className="w-full sm:w-56">
                <Select
                  value={selectedSubjectId}
                  onValueChange={setSelectedSubjectId}
                  disabled={filteredSubjectsForFilter.length === 0}
                >
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="All Subjects" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Subjects</SelectItem>
                    {filteredSubjectsForFilter.map((s: any) => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Search */}
              <div className="w-full sm:w-64">
                <Input
                  placeholder="Search chapter name..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="h-9"
                />
              </div>
            </div>

            <div className="text-xs text-muted-foreground whitespace-nowrap">
              Total: <span className="font-semibold text-foreground">{chaptersData?.total || 0}</span> chapters
            </div>
          </div>

          <div className="rounded-lg border bg-card overflow-hidden">
            <DataTable
              columns={chapterColumns}
              rows={chaptersData?.data || []}
              loading={isFetchingChapters}
              total={chaptersData?.total || (chaptersData?.data?.length ?? 0)}
              page={chapterPage}
              limit={chapterLimit}
              onPageChange={setChapterPage}
              onLimitChange={setChapterLimit}
            />
          </div>
        </TabsContent>

        {/* TAB 2: PURCHASE RECORDS */}
        <TabsContent value="orders" className="space-y-4 pt-4">
          <div className="flex items-center justify-between">
            <Input
              placeholder="Search by student name or mobile..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="h-9 max-w-sm"
            />
            <div className="text-xs text-muted-foreground">
              Total Transactions: <span className="font-semibold text-foreground">{ordersData?.total || 0}</span>
            </div>
          </div>

          <div className="rounded-lg border bg-card overflow-hidden">
            <DataTable
              columns={orderColumns}
              rows={ordersData?.data || []}
              loading={isFetchingOrders}
              total={ordersData?.total || (ordersData?.data?.length ?? 0)}
              page={orderPage}
              limit={orderLimit}
              onPageChange={setOrderPage}
              onLimitChange={setOrderLimit}
            />
          </div>
        </TabsContent>
      </Tabs>

      {/* CREATE / EDIT CHAPTER DIALOG */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingChapter ? "Edit Chapter Purchase Plan" : "Create Purchasable Chapter Plan"}
            </DialogTitle>
            <DialogDescription>
              Each chapter acts as an individual purchasable product granting lifetime access to its Notes, Videos, and Exams.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={form.handleSubmit(onChapterSubmit)} className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Academic Year *</Label>
                <Select
                  value={form.watch("academicYearId") || undefined}
                  onValueChange={(val) => {
                    form.setValue("academicYearId", val);
                    form.setValue("subjectId", "");
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select Year" />
                  </SelectTrigger>
                  <SelectContent>
                    {yearsData?.data?.map((y: any) => (
                      <SelectItem key={y.id} value={y.id}>
                        {y.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {form.formState.errors.academicYearId && (
                  <p className="text-xs text-destructive">{form.formState.errors.academicYearId.message}</p>
                )}
              </div>

              <div className="space-y-1.5">
                <Label>Subject *</Label>
                <Select
                  value={form.watch("subjectId") || undefined}
                  onValueChange={(val) => form.setValue("subjectId", val)}
                  disabled={filteredSubjectsForModal.length === 0}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select Subject" />
                  </SelectTrigger>
                  <SelectContent>
                    {filteredSubjectsForModal.map((s: any) => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {form.formState.errors.subjectId && (
                  <p className="text-xs text-destructive">{form.formState.errors.subjectId.message}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2 space-y-1.5">
                <Label>Chapter Name *</Label>
                <Input {...form.register("name")} placeholder="e.g. Introduction to Dravyaguna" />
                {form.formState.errors.name && (
                  <p className="text-xs text-destructive">{form.formState.errors.name.message}</p>
                )}
              </div>

              <div className="space-y-1.5">
                <Label>Order # *</Label>
                <Input type="number" {...form.register("number")} min={1} />
                {form.formState.errors.number && (
                  <p className="text-xs text-destructive">{form.formState.errors.number.message}</p>
                )}
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="flex items-center justify-between">
                <span>Lifetime Purchase Price (₹) *</span>
                <span className="text-xs text-emerald-600 font-semibold">1-time payment</span>
              </Label>
              <div className="relative">
                <IndianRupee className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  type="number"
                  {...form.register("price")}
                  min={0}
                  className="pl-9 font-bold"
                  placeholder="199"
                />
              </div>
              <p className="text-[11px] text-muted-foreground">
                Set ₹0 for a completely free chapter. When purchased, all Notes, Videos, and Exams unlock permanently.
              </p>
              {form.formState.errors.price && (
                <p className="text-xs text-destructive">{form.formState.errors.price.message}</p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label>Chapter Description</Label>
              <Textarea
                {...form.register("description")}
                placeholder="Key concepts, syllabus coverage, and learning outcomes..."
                rows={2}
              />
            </div>

            <div className="flex items-center justify-between rounded-lg border p-3">
              <div className="space-y-0.5">
                <Label className="text-sm">Available for Purchase</Label>
                <p className="text-xs text-muted-foreground">Students can browse and buy this chapter.</p>
              </div>
              <Switch
                checked={form.watch("isActive")}
                onCheckedChange={(val) => form.setValue("isActive", val)}
              />
            </div>

            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={createStatus.isLoading || updateStatus.isLoading}>
                {editingChapter ? "Update Chapter Plan" : "Create Chapter Plan"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* QUICK PRICE EDIT DIALOG */}
      <Dialog open={!!priceModalChapter} onOpenChange={(open) => !open && setPriceModalChapter(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Update Chapter Price</DialogTitle>
            <DialogDescription>
              Set the individual lifetime purchase price for <span className="font-semibold text-foreground">{priceModalChapter?.name}</span>.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={priceForm.handleSubmit(onPriceSubmit)} className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>New Price (₹)</Label>
              <div className="relative">
                <IndianRupee className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  type="number"
                  {...priceForm.register("price")}
                  min={0}
                  className="pl-9 font-bold text-lg"
                  placeholder="199"
                  autoFocus
                />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setPriceModalChapter(null)}>
                Cancel
              </Button>
              <Button type="submit" disabled={updateStatus.isLoading}>
                Save Price
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* CONFIRM DELETE DIALOG */}
      <ConfirmDeleteDialog
        open={!!toDeleteChapter}
        onOpenChange={(open) => !open && setToDeleteChapter(null)}
        onConfirm={handleDeleteChapter}
        title="Delete Chapter Plan"
        description={`Are you sure you want to delete "${toDeleteChapter?.name}"? All content linked to this chapter may become orphaned.`}
        isDeleting={deleteStatus.isLoading}
      />
    </div>
  );
}
